// Periodenabschluss, Buchungsjournal (Leseansicht), Export im DATEV-Format (Buchungsstapel, EXTF-CSV) und die
// Kontenzuordnung. Das Journal entsteht aus den Belegen und Zahlungen – es gibt kein Hauptbuch, das man bebuchen könnte.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zDay, zMonth, zReason } from './common'

const EXPORT_READ = 'finance.export.read'
const EXPORT_RUN = 'finance.export.run'
const MAPPING = 'finance.export.mapping'

// ---- Periodenabschluss ---------------------------------------------------------------------------------------

export const zPeriodCheck = z.object({
  key: z.string(),
  label: z.string(),
  /** ok | warning | blocking */
  state: z.enum(['ok', 'warning', 'blocking']),
  count: z.int(),
  detail: z.string(),
  /** Seite, auf der sich der Punkt erledigen lässt */
  href: z.string().nullable(),
  examples: z.array(z.string()),
})

export const periodStatus = defineContract({
  name: 'finance.period.status',
  title: 'Periodenabschluss anzeigen',
  description: 'Bis wann abgeschlossen ist, die Prüfliste für den nächsten Monat und der Verlauf der Abschlüsse.',
  llm: {
    description:
      'Zeigt den Stand des Periodenabschlusses: bis zu welchem Tag Belege und Zahlungen gesperrt sind, wer zuletzt ' +
      'abgeschlossen oder geöffnet hat, und die Prüfliste für einen Monat (Standard: der nächste offene): nicht ' +
      'festgeschriebene Belege, nicht berechnete Lieferungen, nicht zugeordnete Zahlungen, offene Differenzen. ' +
      'Verändert nichts. Abschließen: finance.period.close.',
    keywords: ['Monatsabschluss', 'Periode', 'abgeschlossen bis', 'Prüfliste', 'Periodensperre'],
  },
  input: z.strictObject({ month: zMonth.optional() }),
  output: z.object({
    lockedUntil: z.string().nullable(),
    /** Monat der Prüfliste 'YYYY-MM' */
    month: z.string(),
    monthLabel: z.string(),
    from: z.string(),
    to: z.string(),
    /** closed | open | current (läuft noch) */
    monthState: z.enum(['closed', 'open', 'current']),
    canClose: z.boolean(),
    blockedReason: z.string().nullable(),
    checks: z.array(zPeriodCheck),
    history: z.array(z.object({ id: zId, lockedUntil: z.string(), kind: z.enum(['close', 'reopen']), reason: z.string().nullable(), byName: z.string().nullable(), at: z.date() })),
    /** Monate zur Auswahl, neuester zuerst */
    months: z.array(z.object({ month: z.string(), label: z.string(), state: z.enum(['closed', 'open', 'current']) })),
  }),
  permission: EXPORT_READ,
  risk: 'read',
})

export const periodClose = defineContract({
  name: 'finance.period.close',
  title: 'Monat abschließen',
  description: 'Sperrt den Monat für Belege und Zahlungen. Danach wird jede Buchung mit einem Datum in diesem Monat abgelehnt.',
  llm: {
    description:
      'Schließt einen Monat ab: Belege und Zahlungen mit einem Datum bis zum Monatsende lassen sich danach nicht mehr ' +
      'buchen (Stornos und Korrekturen laufen als neue Belege mit einem Datum im offenen Monat). Geht nur für vergangene Monate, ' +
      'lückenlos nacheinander, und nur wenn die Prüfliste keinen sperrenden Punkt hat (finance.period.status). ' +
      'Öffnen kann nur, wer das starke Recht dazu hält (finance.period.reopen).',
    keywords: ['Monat abschließen', 'Periode schließen', 'Monatsabschluss', 'Buchungssperre'],
  },
  input: z.strictObject({ month: zMonth, note: z.string().trim().max(300).nullish() }),
  output: z.object({ lockedUntil: z.string(), monthLabel: z.string(), warnings: z.array(z.string()), message: z.string() }),
  permission: 'finance.period.close',
  risk: 'destructive',
})

export const periodReopen = defineContract({
  name: 'finance.period.reopen',
  title: 'Abgeschlossenen Monat wieder öffnen',
  description: 'Hebt den Abschluss ab einem Monat auf – nur mit dem starken Recht und einer Begründung; der Vorgang bleibt sichtbar.',
  llm: {
    description:
      'Öffnet einen abgeschlossenen Monat (und alle späteren) wieder. Braucht eine ausführliche Begründung und das ' +
      'Recht „Abgeschlossenen Monat wieder öffnen“. Wurde der Zeitraum schon exportiert, nennt die Vorschau die ' +
      'Exporte: Die Steuerkanzlei hat diese Zahlen bereits. Nur verwenden, wenn ein Beleg im abgeschlossenen Monat ' +
      'zwingend fehlt – sonst im offenen Monat korrigieren.',
    keywords: ['Monat öffnen', 'Abschluss aufheben', 'Periode wieder öffnen'],
  },
  input: z.strictObject({
    month: zMonth,
    reason: z
      .string({ error: 'Bitte begründen Sie, warum der Monat wieder geöffnet wird.' })
      .trim()
      .min(10, { error: 'Bitte begründen Sie das Öffnen ausführlich (mindestens 10 Zeichen).' })
      .max(500),
  }),
  output: z.object({ lockedUntil: z.string().nullable(), monthLabel: z.string(), warnings: z.array(z.string()), message: z.string() }),
  permission: 'finance.period.reopen',
  risk: 'destructive',
  effects: { securitySensitive: true },
})

// ---- Buchungsjournal ---------------------------------------------------------------------------------------------

export const JOURNAL_KINDS = [
  'revenue',
  'deposit',
  'purchase',
  'deposit_purchase',
  'fee',
  'payment',
  'discount',
  'write_off',
  'offset',
  // Kasse (je abgeschlossener Schicht): Zahlungen in die Kasse, Wertgutscheine, Kassendifferenz, Bargeldbewegungen
  'till_payment',
  'voucher',
  'cash_difference',
  'cash_movement',
  // Bankabgleich: Umsatz ohne offenen Posten auf ein Sachkonto (finance.bank.book) und sein Storno
  'bank',
] as const

export const zJournalRow = z.object({
  key: z.string(),
  date: z.string(),
  kind: z.enum(JOURNAL_KINDS),
  kindLabel: z.string(),
  /** Belegnummer (Belegfeld 1) */
  number: z.string().nullable(),
  text: z.string(),
  debitAccount: z.string().nullable(),
  debitName: z.string(),
  creditAccount: z.string().nullable(),
  creditName: z.string(),
  /** Betrag in Cent, immer positiv (brutto bei Erlös und Wareneingang) */
  amountCents: z.int(),
  /** Steuerschlüssel der Buchung (BU-Schlüssel) – leer bei Automatikkonten */
  taxKey: z.string().nullable(),
  taxRateBp: z.int().nullable(),
  partnerName: z.string().nullable(),
  costCenter: z.string().nullable(),
  documentId: zId.nullable(),
  documentType: z.string().nullable(),
  paymentId: zId.nullable(),
  /** Kassenschicht, aus deren Abschluss die Buchung stammt (sonst null) */
  shiftId: zId.nullable(),
  /** enthaltene Umsatzsteuer in Cent – nur bei Erlös- und Pfandbuchungen der Kasse, sonst null */
  taxCents: z.int().nullable(),
  /** nicht zugeordnet: Konto fehlt in der Kontenzuordnung */
  unmapped: z.boolean(),
})

export const zJournalIssue = z.object({
  /** mapping | partner | setting | number */
  kind: z.string(),
  message: z.string(),
  count: z.int(),
  /** Weg zur Behebung */
  href: z.string().nullable(),
})

export const journalList = defineContract({
  name: 'finance.journal.list',
  title: 'Buchungsjournal anzeigen',
  description: 'Jede Erlös-, Wareneingangs-, Zahlungs- und Skontobuchung eines Zeitraums – abgeleitet aus den Belegen und den abgeschlossenen Kassenschichten, mit den zugeordneten Konten.',
  llm: {
    description:
      'Leitet das Buchungsjournal eines Zeitraums aus den festgeschriebenen Belegen, den gebuchten Zahlungen und den ' +
      'abgeschlossenen Kassenschichten ab (je Schicht und Steuersatz Erlöse, Gutscheine, Bar- und ' +
      'Kartenzahlungen, Kassendifferenz, Bargeldbewegungen – eine offene Schicht fehlt, bis sie abgeschlossen ist): je ' +
      'Buchung Datum, Belegnummer, Text, Sollkonto, Habenkonto, Betrag in Cent (brutto), Steuerschlüssel. Die Konten ' +
      'kommen aus der Kontenzuordnung; was dort fehlt, steht unter `issues`. Reine Leseansicht – gebucht wird nur über ' +
      'Belege und Zahlungen. Summen je Konto unter `accounts`. Die Datei für die Steuerkanzlei: finance.export.run.',
    keywords: ['Buchungsjournal', 'Buchungen', 'Erlöse', 'Konten', 'Journal', 'Summen je Konto'],
  },
  input: z.strictObject({ from: zDay, to: zDay, limit: z.int().min(1).max(5000).default(2000) }),
  output: z.object({
    from: z.string(),
    to: z.string(),
    rows: z.array(zJournalRow),
    truncated: z.boolean(),
    totals: z.object({
      count: z.int(),
      amountCents: z.int(),
      revenueCents: z.int(),
      purchaseCents: z.int(),
      paymentInCents: z.int(),
      paymentOutCents: z.int(),
      /** davon aus Kassenabschlüssen: Erlöse und Pfand brutto (in revenueCents enthalten) und Zahl der Schichten */
      tillRevenueCents: z.int(),
      shiftCount: z.int(),
    }),
    accounts: z.array(z.object({ account: z.string().nullable(), name: z.string(), debitCents: z.int(), creditCents: z.int(), balanceCents: z.int() })),
    issues: z.array(zJournalIssue),
  }),
  permission: EXPORT_READ,
  risk: 'read',
})

// ---- Export --------------------------------------------------------------------------------------------------------

export const exportValidate = defineContract({
  name: 'finance.export.validate',
  title: 'Export prüfen',
  description: 'Prüft einen Zeitraum vor dem Export: Summen des Journals und alles, was nicht zugeordnet ist.',
  llm: {
    description:
      'Prüft, ob sich ein Zeitraum als Buchungsstapel exportieren lässt: Anzahl und Summe der Buchungen, enthaltene ' +
      'Belege, und der Prüfbericht – fehlende Konten in der Kontenzuordnung, Partner ohne Debitoren- oder ' +
      'Kreditorennummer, fehlende Berater- oder Mandantennummer, Belegnummern, die das Format nicht zulässt. Nennt ' +
      'auch frühere Exporte desselben Zeitraums. Verändert nichts. Exportieren: finance.export.run.',
    keywords: ['Export prüfen', 'DATEV prüfen', 'Prüfbericht', 'nicht zugeordnet'],
  },
  input: z.strictObject({ from: zDay, to: zDay }),
  output: z.object({
    from: z.string(),
    to: z.string(),
    bookingCount: z.int(),
    documentCount: z.int(),
    paymentCount: z.int(),
    /** abgeschlossene Kassenschichten im Zeitraum */
    shiftCount: z.int(),
    amountCents: z.int(),
    exportable: z.boolean(),
    issues: z.array(zJournalIssue),
    /** frühere Exporte genau dieses Zeitraums */
    previous: z.array(z.object({ id: zId, createdAt: z.date(), byName: z.string().nullable(), sha256: z.string().nullable() })),
    periodClosed: z.boolean(),
    notice: z.string(),
  }),
  permission: EXPORT_READ,
  risk: 'read',
})

const zExportRun = z.object({
  id: zId,
  from: z.string(),
  to: z.string(),
  createdAt: z.date(),
  byName: z.string().nullable(),
  fileId: zId.nullable(),
  fileName: z.string().nullable(),
  /** Adresse zum Laden der Datei */
  fileUrl: z.string().nullable(),
  sha256: z.string().nullable(),
  documentCount: z.int(),
  bookingCount: z.int(),
  amountCents: z.int(),
  /** Wiederholung: Dieser Zeitraum wurde schon einmal exportiert */
  repeat: z.boolean(),
  repeatNo: z.int(),
  /** Buchungen sind dieselben wie beim vorigen Export des Zeitraums */
  sameContent: z.boolean().nullable(),
  chart: z.string().nullable(),
})

export const exportRun = defineContract({
  name: 'finance.export.run',
  title: 'Buchungsstapel exportieren',
  description: 'Erzeugt den Buchungsstapel eines Zeitraums als Datei im DATEV-Format (EXTF-CSV) und hält den Export mit Belegen und Prüfsumme fest.',
  llm: {
    description:
      'Erzeugt für einen Zeitraum den Buchungsstapel als CSV-Datei im DATEV-Format (EXTF, Windows-1252): Debitoren- und ' +
      'Kreditorennummern, Konten und Steuerschlüssel aus der Kontenzuordnung, Belegnummer und Belegdatum. Der Export ' +
      'wird mit enthaltenen Belegen und Prüfsumme festgehalten; ein zweiter Export desselben Zeitraums ist als ' +
      'Wiederholung gekennzeichnet. Solange der Prüfbericht Fehler nennt (finance.export.validate), entsteht keine ' +
      'Datei. Das Format ist vor dem ersten Echtlauf mit der Steuerkanzlei abzustimmen; übertragen wird nichts.',
    keywords: ['DATEV-Export', 'Buchungsstapel', 'Steuerberater', 'exportieren', 'EXTF'],
  },
  input: z.strictObject({ from: zDay, to: zDay }),
  output: zExportRun.extend({ message: z.string() }),
  permission: EXPORT_RUN,
  risk: 'write',
  effects: { external: true },
})

export const exportList = defineContract({
  name: 'finance.export.list',
  title: 'Exporte anzeigen',
  description: 'Bisherige Exporte mit Zeitraum, Ersteller, Prüfsumme und der Kennzeichnung von Wiederholungen.',
  llm: {
    description: 'Listet die bisherigen Buchungsstapel-Exporte, neueste zuerst: Zeitraum, erzeugt von und am, Zahl der Belege und Buchungen, Summe in Cent, Prüfsumme der Datei, Wiederholung ja/nein.',
    keywords: ['Exporte', 'Exportprotokoll', 'schon exportiert', 'DATEV'],
  },
  input: z.strictObject({ limit: z.int().min(1).max(200).default(50) }),
  output: z.object({
    rows: z.array(zExportRun),
    /** Exporte der Debitoren/Kreditoren, neueste zuerst */
    partnerRuns: z.array(z.object({ id: zId, createdAt: z.date(), byName: z.string().nullable(), fileName: z.string().nullable(), fileUrl: z.string().nullable(), sha256: z.string().nullable(), count: z.int() })),
    notice: z.string(),
  }),
  permission: EXPORT_READ,
  risk: 'read',
})

export const exportPartners = defineContract({
  name: 'finance.export.partners',
  title: 'Debitoren und Kreditoren exportieren',
  description: 'Erzeugt die Stammdaten der Personenkonten als Datei im DATEV-Format (Debitoren/Kreditoren) – Name, Anschrift, USt-IdNr., Bankverbindung.',
  llm: {
    description:
      'Erzeugt die Stammdaten aller Kunden mit Debitorennummer und aller Lieferanten mit Kreditorennummer als CSV-Datei im ' +
      'DATEV-Format „Debitoren/Kreditoren“ (EXTF, Formatkategorie 16, Windows-1252): Personenkonto, Name, Adressattyp, ' +
      'USt-IdNr., Anschrift, Telefon, E-Mail, IBAN/BIC, Kunden- bzw. Lieferantennummer. `scope`: all, customers, suppliers. ' +
      'Partner ohne Personenkonto fehlen in der Datei und stehen im Ergebnis. Format vor dem ersten Echtlauf mit der ' +
      'Steuerkanzlei abstimmen; übertragen wird nichts.',
    keywords: ['Debitoren exportieren', 'Kreditoren exportieren', 'Stammdaten DATEV', 'Personenkonten'],
  },
  input: z.strictObject({ scope: z.enum(['all', 'customers', 'suppliers']).default('all') }),
  output: z.object({
    id: zId,
    fileId: zId.nullable(),
    fileName: z.string(),
    fileUrl: z.string().nullable(),
    sha256: z.string().nullable(),
    count: z.int(),
    customerCount: z.int(),
    supplierCount: z.int(),
    /** Partner ohne Personenkonto oder mit falscher Stellenzahl – nicht in der Datei */
    skipped: z.array(z.object({ name: z.string(), number: z.string().nullable(), reason: z.string() })),
    message: z.string(),
  }),
  permission: EXPORT_RUN,
  risk: 'write',
  effects: { external: true },
})

// ---- Kontenzuordnung und Angaben für den Export --------------------------------------------------------------------

export const ACCOUNT_PURPOSES = [
  'revenue',
  'deposit_sales',
  'purchase',
  'deposit_purchase',
  'payment',
  'discount_granted',
  'discount_received',
  'fee',
  'rounding',
  // Kasse (Migration 1701)
  'pos_clearing',
  'voucher',
  'cash_transit',
  'cash_difference',
  'cash_payout',
  // Leergut, das Kunden zurückgeben – ohne eigene Zeile gilt das Konto für Pfand im Verkauf (Migration 1703)
  'deposit_return',
] as const
export type AccountPurpose = (typeof ACCOUNT_PURPOSES)[number]

export const CHARTS = ['skr03', 'skr04'] as const
export type Chart = (typeof CHARTS)[number]

const zAccount = z.string({ error: 'Bitte ein Konto eingeben.' }).trim().regex(/^[0-9]{3,9}$/, { error: 'Das Konto besteht aus 3 bis 9 Ziffern.' })
const zTaxKey = z.string().trim().regex(/^[0-9]{1,4}$/, { error: 'Der Steuerschlüssel besteht aus 1 bis 4 Ziffern.' })

export const zMappingRow = z.object({
  /** Schlüssel der Zeile: Zweck und wofür genau */
  key: z.string(),
  purpose: z.enum(ACCOUNT_PURPOSES),
  purposeLabel: z.string(),
  /** „19 % Umsatzsteuer“, „Überweisung“, „allgemein“ */
  subject: z.string(),
  taxCodeId: zId.nullable(),
  paymentMethod: z.string().nullable(),
  id: zId.nullable(),
  account: z.string().nullable(),
  taxKey: z.string().nullable(),
  /** Vorschlag der gewählten Vorlage */
  suggestedAccount: z.string().nullable(),
  suggestedTaxKey: z.string().nullable(),
  /** in den Belegen des laufenden Jahres gebraucht */
  required: z.boolean(),
  version: z.int().nullable(),
})

const zDatevSettings = z.object({
  chart: z.enum(CHARTS).nullable(),
  consultantNo: z.string().nullable(),
  clientNo: z.string().nullable(),
  /** Beginn des Wirtschaftsjahres: Monat 1–12 */
  fiscalYearStartMonth: z.int(),
  /** Länge der Sachkonten (Personenkonten eine Stelle länger) */
  accountLength: z.int(),
  /** Festschreibung beim Import in der Kanzlei (Kopf Feld 21) */
  locked: z.boolean(),
  /** Diktatkürzel */
  dictationCode: z.string().nullable(),
})

export const accountMappingGet = defineContract({
  name: 'finance.account.mapping',
  title: 'Kontenzuordnung anzeigen',
  description: 'Welches Konto und welcher Steuerschlüssel für Erlöse, Wareneingang, Zahlarten, Skonto, Gebühren und die Kasse gilt.',
  llm: {
    description:
      'Liefert die Kontenzuordnung für das Buchungsjournal und den Export: je Zweck (Erlöse, ' +
      'Wareneingang, Zahlarten, Skonto, Mahngebühren, Kleindifferenzen, Kasse: Verrechnungskonto, ' +
      'Wertgutscheine, Geldtransit, Kassendifferenzen, Auszahlungen) und Steuerschlüssel bzw. ' +
      'Zahlart das Sachkonto und den Steuerschlüssel, dazu den Vorschlag der Vorlage (SKR03 oder SKR04) und die ' +
      'Angaben für den Export (Berater-, Mandantennummer, Wirtschaftsjahr). Verändert nichts.',
    keywords: ['Kontenzuordnung', 'Kontenrahmen', 'SKR03', 'SKR04', 'Erlöskonto', 'Sachkonto'],
  },
  input: z.strictObject({}),
  output: z.object({ settings: zDatevSettings, settingsVersion: z.int(), rows: z.array(zMappingRow), missingCount: z.int(), notice: z.string() }),
  permission: EXPORT_READ,
  risk: 'read',
})

export const accountMappingSet = defineContract({
  name: 'finance.account.set',
  title: 'Kontenzuordnung ändern',
  description: 'Setzt Sachkonto und Steuerschlüssel für einen Zweck – oder nimmt die Zuordnung zurück.',
  llm: {
    description:
      'Ändert Zeilen der Kontenzuordnung: je Zeile Zweck, wofür genau (Steuerschlüssel der Ware ODER Zahlart, sonst ' +
      'allgemein), Sachkonto (3–9 Ziffern) und optional den Steuerschlüssel der Buchung; `account: null` entfernt die ' +
      'Zuordnung. Wirkt auf Journal und künftige Exporte – bereits erzeugte Exportdateien ändern sich nicht. Ganze ' +
      'Vorlage übernehmen: finance.account.applyTemplate.',
    keywords: ['Konto zuordnen', 'Erlöskonto ändern', 'Kontenzuordnung ändern'],
  },
  input: z.strictObject({
    rows: z
      .array(
        z.strictObject({
          purpose: z.enum(ACCOUNT_PURPOSES),
          taxCodeId: zId.nullish(),
          paymentMethod: z.string().max(40).nullish(),
          account: zAccount.nullable(),
          taxKey: zTaxKey.nullish(),
        }),
      )
      .min(1)
      .max(100),
  }),
  output: z.object({ changed: z.int(), message: z.string() }),
  permission: MAPPING,
  risk: 'financial',
  limits: { maxItems: 100, itemCount: (input) => input.rows.length },
})

export const accountApplyTemplate = defineContract({
  name: 'finance.account.applyTemplate',
  title: 'Kontenrahmen-Vorlage übernehmen',
  description: 'Füllt die Kontenzuordnung mit den Vorschlägen für SKR03 oder SKR04 – als Ausgangspunkt für die Abstimmung mit der Steuerkanzlei.',
  llm: {
    description:
      'Übernimmt die Vorschläge einer Vorlage (skr03 oder skr04) in die Kontenzuordnung und merkt den Kontenrahmen. ' +
      '`overwrite: false` (Standard) füllt nur, was noch leer ist; `true` ersetzt auch gepflegte Zeilen. Die Konten ' +
      'sind Vorschläge – vor dem ersten Export mit der Steuerkanzlei abstimmen.',
    keywords: ['SKR03', 'SKR04', 'Kontenrahmen', 'Vorlage übernehmen', 'Standardkonten'],
  },
  input: z.strictObject({ chart: z.enum(CHARTS), overwrite: z.boolean().default(false) }),
  output: z.object({ chart: z.enum(CHARTS), changed: z.int(), message: z.string() }),
  permission: MAPPING,
  risk: 'financial',
})

export const exportSettingsUpdate = defineContract({
  name: 'finance.export.settingsUpdate',
  title: 'Angaben für den Export ändern',
  description: 'Berater- und Mandantennummer, Beginn des Wirtschaftsjahres und Länge der Sachkonten.',
  llm: {
    description: 'Ändert die Angaben im Kopf der Exportdatei: Beraternummer (1001–9999999) und Mandantennummer (1–99999) der Steuerkanzlei, Monat, in dem das Wirtschaftsjahr beginnt (1–12), Länge der Sachkonten (4–8), ob die Kanzlei den Stapel beim Import festschreibt (`locked`, Standard nein) und ein Diktatkürzel (zwei Großbuchstaben). Die Werte nennt die Steuerkanzlei.',
    keywords: ['Beraternummer', 'Mandantennummer', 'Wirtschaftsjahr', 'Sachkontenlänge', 'Festschreibung'],
  },
  input: z.strictObject({
    consultantNo: z.string().trim().regex(/^[0-9]{4,7}$/, { error: 'Die Beraternummer besteht aus 4 bis 7 Ziffern.' }).refine((value) => Number(value) >= 1001, { error: 'Die Beraternummer liegt zwischen 1001 und 9999999.' }).nullish(),
    clientNo: z.string().trim().regex(/^[0-9]{1,5}$/, { error: 'Die Mandantennummer besteht aus 1 bis 5 Ziffern.' }).nullish(),
    fiscalYearStartMonth: z.int().min(1).max(12).optional(),
    accountLength: z.int().min(4, { error: 'Sachkonten haben 4 bis 8 Stellen.' }).max(8, { error: 'Sachkonten haben 4 bis 8 Stellen.' }).optional(),
    locked: z.boolean().optional(),
    dictationCode: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, { error: 'Das Diktatkürzel besteht aus zwei Buchstaben.' }).nullish(),
    expectedVersion: z.int().optional(),
  }),
  output: z.object({ settings: zDatevSettings, settingsVersion: z.int() }),
  permission: MAPPING,
  risk: 'write',
})
